import { useState, useEffect } from "react";
import { Navigate } from "react-router";
import { useAuth } from "../../hooks/useAuth";
import {
  getServices,
  createService,
  updateService,
  deleteService,
} from "../../api/service.api";
import { apiClient } from "../../api/client";
import { ServiceCard } from "../../components/booking/ServiceCard";
import { EmptyState } from "../../components/ui/EmptyState";
import type { Service, BusinessProfile } from "@abejon/shared";

export function ServicesPage() {
  const { user, isLoading, isAuthenticated } = useAuth();
  const [businessId, setBusinessId] = useState<string | null>(null);
  const [serviceList, setServiceList] = useState<Service[]>([]);
  const [loadingServices, setLoadingServices] = useState(true);
  const [editing, setEditing] = useState<Service | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  // Form state
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [durationMinutes, setDurationMinutes] = useState(30);
  const [priceInCents, setPriceInCents] = useState(0);
  const [priceDisplay, setPriceDisplay] = useState("");

  // Load business profile and services
  useEffect(() => {
    if (!user || user.accountType !== "business") return;
    let cancelled = false;

    async function load() {
      try {
        // Get the user's business profile
        const data = await apiClient<{ businesses: BusinessProfile[] }>(
          "/businesses?mine=true"
        ).catch(() => null);

        if (cancelled) return;

        if (data?.businesses?.[0]) {
          setBusinessId(data.businesses[0].id);
          const servicesData = await getServices(data.businesses[0].id);
          if (!cancelled) setServiceList(servicesData.services);
        }
      } catch {
        // No business profile yet
      } finally {
        if (!cancelled) setLoadingServices(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
      </div>
    );
  }

  if (!isAuthenticated || user?.accountType !== "business") {
    return <Navigate to="/" replace />;
  }

  function resetForm() {
    setName("");
    setDescription("");
    setDurationMinutes(30);
    setPriceInCents(0);
    setPriceDisplay("");
    setEditing(null);
    setShowForm(false);
    setMessage("");
  }

  function handleEdit(service: Service) {
    setEditing(service);
    setName(service.name);
    setDescription(service.description ?? "");
    setDurationMinutes(service.durationMinutes);
    setPriceInCents(service.priceInCents);
    setPriceDisplay((service.priceInCents / 100).toFixed(2));
    setShowForm(true);
    setMessage("");
  }

  async function handleDelete(service: Service) {
    if (!confirm(`Delete "${service.name}"?`)) return;

    try {
      await deleteService(service.id);
      setServiceList((prev) => prev.filter((s) => s.id !== service.id));
      setMessage("Service deleted");
    } catch {
      setMessage("Failed to delete service");
    }
  }

  function handlePriceChange(value: string) {
    // Allow only numbers and one decimal point
    const cleaned = value.replace(/[^0-9.]/g, "");
    setPriceDisplay(cleaned);
    const dollars = parseFloat(cleaned);
    if (!isNaN(dollars)) {
      setPriceInCents(Math.round(dollars * 100));
    } else {
      setPriceInCents(0);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setMessage("");

    try {
      if (editing) {
        const { service: updated } = await updateService(editing.id, {
          name,
          description: description || undefined,
          durationMinutes,
          priceInCents,
        });
        setServiceList((prev) =>
          prev.map((s) => (s.id === updated.id ? updated : s))
        );
        setMessage("Service updated");
      } else {
        const { service: created } = await createService({
          name,
          description: description || undefined,
          durationMinutes,
          priceInCents,
        });
        setServiceList((prev) => [...prev, created]);
        setMessage("Service created");
      }
      resetForm();
    } catch {
      setMessage("Failed to save service");
    } finally {
      setSaving(false);
    }
  }

  if (loadingServices) {
    return (
      <div className="flex justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-gray-200 border-t-gray-900" />
      </div>
    );
  }

  if (!businessId) {
    return (
      <div className="py-16 text-center">
        <h1 className="text-xl font-bold text-gray-900">No Business Profile</h1>
        <p className="mt-2 text-sm text-gray-500">
          Create a business profile first in Settings.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg py-8">
      <div className="mb-6 flex items-center justify-between">
        <h1 className="text-2xl font-bold">Services</h1>
        {!showForm && (
          <button
            onClick={() => { resetForm(); setShowForm(true); }}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800"
          >
            Add Service
          </button>
        )}
      </div>

      {message && (
        <p className={`mb-4 text-sm ${message.includes("Failed") ? "text-red-600" : "text-green-600"}`}>
          {message}
        </p>
      )}

      {showForm && (
        <form onSubmit={handleSubmit} className="mb-6 space-y-4 rounded-lg border border-gray-200 p-4">
          <h2 className="text-lg font-semibold">
            {editing ? "Edit Service" : "New Service"}
          </h2>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Service Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              maxLength={100}
              placeholder="e.g. Classic Haircut"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-gray-700">
              Description (optional)
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder="Brief description of the service"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Duration (minutes)
              </label>
              <input
                type="number"
                value={durationMinutes}
                onChange={(e) => setDurationMinutes(parseInt(e.target.value) || 0)}
                required
                min={5}
                max={480}
                step={5}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Price ($)
              </label>
              <input
                type="text"
                inputMode="decimal"
                value={priceDisplay}
                onChange={(e) => handlePriceChange(e.target.value)}
                required
                placeholder="0.00"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-gray-900 focus:outline-none focus:ring-1 focus:ring-gray-900"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <button
              type="submit"
              disabled={saving}
              className="flex-1 rounded-lg bg-gray-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-gray-800 disabled:opacity-50"
            >
              {saving ? "Saving..." : editing ? "Update" : "Create"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {serviceList.length === 0 && !showForm ? (
        <EmptyState
          title="No services yet"
          description="Add your first service to start accepting bookings."
          action={{ label: "Add Service", onClick: () => { resetForm(); setShowForm(true); } }}
        />
      ) : (
        <div className="space-y-3">
          {serviceList.map((s) => (
            <ServiceCard
              key={s.id}
              service={s}
              showActions
              onEdit={handleEdit}
              onDelete={handleDelete}
            />
          ))}
        </div>
      )}
    </div>
  );
}
